import { useAtom } from 'jotai'
import { useEffect } from 'react'
import type { LoaderFunctionArgs } from 'react-router'
import {
  redirect,
  useLoaderData,
  useNavigate,
  useNavigation,
} from 'react-router'
import { aSelectedListing } from '~/atoms'
import { Button } from '~/components/ui'
import { cn } from '~/lib'
import { getSubdomainClient, prepareListing } from '~/lib.server'
import { activityHelper, findListing } from '~/mongo.server'
import MapPreviewListing from './listing-preview'

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const { slug } = params
  if (!slug || slug.length < 8) {
    return redirect('/')
  }

  const $or: {
    id?: string
    slug?: string
    tid?: string
  }[] = [{ slug }, { id: slug }]
  if (slug && slug.length >= 8) {
    // At least 9 characters long, so tid and dash
    $or.push({ tid: slug.split('-')[0] })
  }
  const [client, listing] = await Promise.all([
    getSubdomainClient(request.url),
    findListing({
      find: { $or },
      project: {},
    }),
  ])

  if (!listing || !client) {
    return { listing: null }
  }

  const prepared = prepareListing(
    listing,
    client!.featured ? client.featured.map((f) => f.id) : [],
    client.listing_labels ?? {},
    client.labels ?? []
  )

  if (request.headers.get('Referer')) {
    await activityHelper(request)
      .source('app')
      .type('preview')
      .related('listing', listing)
      .write()
  }

  return {
    listing: {
      ...prepared,
      labels: prepared.labels.filter((label) => label !== undefined),
    },
  }
}

export default function MapListingRoute(className: string) {
  const navigation = useNavigation()
  const navigate = useNavigate()
  const [_, setSelectedListing] = useAtom(aSelectedListing)

  const { listing: selectedListing } = useLoaderData<typeof loader>()

  useEffect(() => {
    setSelectedListing(selectedListing)
  }, [selectedListing, setSelectedListing])

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 top-0 z-50 overflow-auto lg:static lg:bottom-auto lg:left-auto lg:right-auto lg:top-auto lg:ml-4 lg:w-[500px] lg:pb-4 2xl:w-[500px]',
        navigation.state === 'loading' && 'animate-pulse'
      )}>
      {selectedListing ? (
        <MapPreviewListing listing={selectedListing} />
      ) : (
        <div className="flex min-h-full select-text flex-col items-center justify-center bg-background md:pb-0 lg:rounded-md lg:shadow-md">
          <div className="space-y-6 pb-32 text-center">
            <h1 className="text-8xl font-bold text-foreground">404</h1>
            <p className="text-2xl text-foreground">Listing not found</p>
            <p className="text-muted-foreground">
              The listing you're looking for doesn't exist.
            </p>
            <Button className="mt-6" onClick={() => navigate('/')}>
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
